import { useEffect, useMemo, useRef, useState } from 'react';
import type { Trip } from '@/services/trips';
import { subscribeToTripExpenses, fetchTripExpenses } from '@/services/expenses';
import { subscribeToTripBookings } from '@/services/bookings';
import type { Expense } from '@/services/expenses';
import type { Booking } from '@/services/bookings';
import {
  getCachedTripData,
  expensesFromOfflineCache,
  bookingsFromOfflineCache,
} from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { convertToBaseCurrency } from '@/utils/currency';
import type { TripBudgetSnapshot } from '@/types/budget';

const aggregateByCurrency = <T extends { amount?: number | null; currency?: string }>(
  items: T[],
  fallbackCurrency: string,
): Record<string, number> => {
  const totals: Record<string, number> = {};
  items.forEach((item) => {
    const amount =
      typeof item.amount === 'number' ? item.amount : Number(item.amount);
    if (!Number.isFinite(amount) || amount === 0) {
      return;
    }
    const currency = (item.currency || fallbackCurrency).toUpperCase();
    totals[currency] = (totals[currency] || 0) + amount;
  });
  return totals;
};

const convertTotals = async (
  totals: Record<string, number>,
  baseCurrency: string,
): Promise<number> => {
  const entries = Object.entries(totals);
  if (entries.length === 0) {
    return 0;
  }

  const amounts = entries.map(([currency, amount]) => ({
    amount,
    currency,
  }));
  return convertToBaseCurrency(amounts, baseCurrency);
};

type VersionsMap = Record<string, number>;

/**
 * Subscribes to bookings/expenses for the provided trips and maintains
 * currency-aware spending snapshots converted to the trip's base currency.
 */
export function useTripBudgetSummaries(
  trips: Trip[],
  enabled: boolean,
): Record<string, TripBudgetSnapshot> {
  const [snapshots, setSnapshots] = useState<Record<string, TripBudgetSnapshot>>({});
  const expenseVersionRef = useRef<VersionsMap>({});
  const bookingVersionRef = useRef<VersionsMap>({});
  const expenseStreamHydratedRef = useRef<Record<string, boolean>>({});
  const bookingStreamHydratedRef = useRef<Record<string, boolean>>({});
  const expenseEmptySinceRef = useRef<Record<string, number | null>>({});
  const bookingEmptySinceRef = useRef<Record<string, number | null>>({});
  const hasSeenNonEmptyExpensesRef = useRef<Record<string, boolean>>({});
  const hasSeenNonEmptyBookingsRef = useRef<Record<string, boolean>>({});
  const expenseEmptyVerifyInFlightRef = useRef<Record<string, boolean>>({});
  const tripsRef = useRef(trips);
  tripsRef.current = trips;

  // Callers often pass `[selectedTrip]` built inline — a new array every render. Key off trip ids +
  // baseCurrency so Firestore snapshot churn does not re-subscribe every frame (was causing max update depth).
  const tripsSyncKey = useMemo(
    () =>
      trips
        .map((t) => `${t.id}:${t.baseCurrency || 'USD'}`)
        .sort()
        .join('|'),
    [trips],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const tripList = tripsRef.current;
    if (tripList.length === 0) {
      return;
    }

    let isCancelled = false;
    const unsubscribers: Array<() => void> = [];

    tripList.forEach((trip) => {
      const baseCurrency = trip.baseCurrency || 'USD';

      const handleExpenses = (expenses: Expense[]) => {
        const totals = aggregateByCurrency(expenses, baseCurrency);
        expenseVersionRef.current[trip.id] =
          (expenseVersionRef.current[trip.id] || 0) + 1;
        const currentVersion = expenseVersionRef.current[trip.id];

        (async () => {
          const converted = await convertTotals(totals, baseCurrency);
          if (isCancelled || expenseVersionRef.current[trip.id] !== currentVersion) {
            return;
          }
          setSnapshots((prev) => {
            const existing = prev[trip.id];
            return {
              ...prev,
              [trip.id]: {
                baseCurrency,
                expensesConverted: converted,
                bookingsConverted: existing?.bookingsConverted ?? 0,
                expenseTotalsByCurrency: totals,
                bookingTotalsByCurrency: existing?.bookingTotalsByCurrency ?? {},
                lastUpdated: Date.now(),
              },
            };
          });
        })().catch((error) => {
          console.error('[useTripBudgetSummaries] Failed to convert expense totals', error);
        });
      };

      const handleBookings = (bookings: Booking[]) => {
        const totals = aggregateByCurrency(bookings, baseCurrency);
        bookingVersionRef.current[trip.id] =
          (bookingVersionRef.current[trip.id] || 0) + 1;
        const currentVersion = bookingVersionRef.current[trip.id];

        (async () => {
          const converted = await convertTotals(totals, baseCurrency);
          if (isCancelled || bookingVersionRef.current[trip.id] !== currentVersion) {
            return;
          }
          setSnapshots((prev) => {
            const existing = prev[trip.id];
            return {
              ...prev,
              [trip.id]: {
                baseCurrency,
                expensesConverted: existing?.expensesConverted ?? 0,
                bookingsConverted: converted,
                expenseTotalsByCurrency: existing?.expenseTotalsByCurrency ?? {},
                bookingTotalsByCurrency: totals,
                lastUpdated: Date.now(),
              },
            };
          });
        })().catch((error) => {
          console.error('[useTripBudgetSummaries] Failed to convert booking totals', error);
        });
      };

      void getCachedTripData(trip.id).then((cached) => {
        if (isCancelled || !cached) return;
        if (cached.expenses?.length && !expenseStreamHydratedRef.current[trip.id]) {
          handleExpenses(expensesFromOfflineCache(cached.expenses as unknown[]));
        }
        if (cached.bookings?.length && !bookingStreamHydratedRef.current[trip.id]) {
          handleBookings(bookingsFromOfflineCache(cached.bookings as unknown[]));
        }
      });

      const expenseUnsub = subscribeToTripExpenses(trip.id, (expenses) => {
        expenseStreamHydratedRef.current[trip.id] = true;
        if (expenses.length > 0) {
          expenseEmptySinceRef.current[trip.id] = null;
          hasSeenNonEmptyExpensesRef.current[trip.id] = true;
        }
        if (networkMonitor.shouldUseOfflineDataFallback() && expenses.length === 0) {
          return;
        }
        if (
          networkMonitor.isOnline() &&
          expenses.length === 0 &&
          hasSeenNonEmptyExpensesRef.current[trip.id]
        ) {
          const now = Date.now();
          const firstEmptyAt = expenseEmptySinceRef.current[trip.id];
          if (firstEmptyAt == null) {
            expenseEmptySinceRef.current[trip.id] = now;
            return;
          }
          if (now - firstEmptyAt < 3500) {
            return;
          }
          if (!expenseEmptyVerifyInFlightRef.current[trip.id]) {
            expenseEmptyVerifyInFlightRef.current[trip.id] = true;
            void fetchTripExpenses(trip.id)
              .then((confirmed) => {
                if (isCancelled) return;
                if (confirmed.length > 0) {
                  expenseEmptySinceRef.current[trip.id] = null;
                  handleExpenses(confirmed);
                } else {
                  handleExpenses([]);
                }
              })
              .catch(() => {
                // Keep prior totals if verification fails.
              })
              .finally(() => {
                expenseEmptyVerifyInFlightRef.current[trip.id] = false;
              });
          }
          return;
        }
        handleExpenses(expenses);
      });
      const bookingUnsub = subscribeToTripBookings(trip.id, (bookings) => {
        bookingStreamHydratedRef.current[trip.id] = true;
        if (bookings.length > 0) {
          bookingEmptySinceRef.current[trip.id] = null;
          hasSeenNonEmptyBookingsRef.current[trip.id] = true;
        }
        if (networkMonitor.shouldUseOfflineDataFallback() && bookings.length === 0) {
          return;
        }
        if (
          networkMonitor.isOnline() &&
          bookings.length === 0 &&
          hasSeenNonEmptyBookingsRef.current[trip.id]
        ) {
          const now = Date.now();
          const firstEmptyAt = bookingEmptySinceRef.current[trip.id];
          if (firstEmptyAt == null) {
            bookingEmptySinceRef.current[trip.id] = now;
            return;
          }
          if (now - firstEmptyAt < 3500) {
            return;
          }
        }
        handleBookings(bookings);
      });

      unsubscribers.push(expenseUnsub, bookingUnsub);
    });

    return () => {
      isCancelled = true;
      unsubscribers.forEach((unsub) => {
        try {
          unsub();
        } catch (error) {
          console.warn('[useTripBudgetSummaries] Failed to unsubscribe', error);
        }
      });
    };
  }, [enabled, tripsSyncKey]);

  return snapshots;
}

/**
 * Computes a single trip's budget snapshot from expenses/bookings the caller
 * already owns — no Firestore subscriptions.
 *
 * Use this in screens that already subscribe to the trip's expenses/bookings
 * (e.g. Wallet) to avoid opening a duplicate pair of streams via
 * `useTripBudgetSummaries`.
 */
export function useLocalTripBudgetSummary(
  trip: Trip | null,
  expenses: Expense[],
  bookings: Booking[],
  enabled: boolean,
): TripBudgetSnapshot | undefined {
  const [snapshot, setSnapshot] = useState<TripBudgetSnapshot | undefined>(undefined);
  const versionRef = useRef(0);

  const tripId = trip?.id;
  const baseCurrency = (trip?.baseCurrency || 'USD').toUpperCase();

  // Stable string keys so we only recompute when the actual aggregation input
  // changes. Re-running JSON.stringify on currency totals (≤10 entries in
  // practice) is cheaper than re-running convertToBaseCurrency fetches.
  const expenseTotals = useMemo(
    () => (enabled && tripId ? aggregateByCurrency(expenses, baseCurrency) : {}),
    [enabled, tripId, expenses, baseCurrency],
  );
  const bookingTotals = useMemo(
    () => (enabled && tripId ? aggregateByCurrency(bookings, baseCurrency) : {}),
    [enabled, tripId, bookings, baseCurrency],
  );
  const expenseTotalsKey = useMemo(() => JSON.stringify(expenseTotals), [expenseTotals]);
  const bookingTotalsKey = useMemo(() => JSON.stringify(bookingTotals), [bookingTotals]);

  // Reset the snapshot when the trip changes or we become disabled so stale
  // data from a previously-selected trip isn't shown against the new trip.
  useEffect(() => {
    if (!enabled || !tripId) {
      setSnapshot(undefined);
      return;
    }
  }, [enabled, tripId]);

  useEffect(() => {
    if (!enabled || !tripId) return;

    versionRef.current += 1;
    const currentVersion = versionRef.current;
    let cancelled = false;

    (async () => {
      try {
        const [expensesConverted, bookingsConverted] = await Promise.all([
          convertTotals(expenseTotals, baseCurrency),
          convertTotals(bookingTotals, baseCurrency),
        ]);
        if (cancelled || versionRef.current !== currentVersion) return;
        setSnapshot({
          baseCurrency,
          expensesConverted,
          bookingsConverted,
          expenseTotalsByCurrency: expenseTotals,
          bookingTotalsByCurrency: bookingTotals,
          lastUpdated: Date.now(),
        });
      } catch (error) {
        console.error('[useLocalTripBudgetSummary] Failed to convert totals', error);
      }
    })();

    return () => {
      cancelled = true;
    };
    // expenseTotalsKey / bookingTotalsKey capture the actual content of the
    // totals maps so we don't re-run on fresh-identity maps with equal data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tripId, baseCurrency, expenseTotalsKey, bookingTotalsKey]);

  return snapshot;
}

